import { useState } from 'react';
import type { Movie } from '../../../mocks/movies';
import type { MovieDetail } from '../../../mocks/movieDetails';
import MoviePaymentModal from '../../movies/components/MoviePaymentModal';

interface Props {
  movie: Movie;
  detail: MovieDetail;
}

export default function MovieHero({ movie, detail }: Props) {
  const [showPayment, setShowPayment] = useState(false);
  const [liked, setLiked] = useState(false);

  const typeLabel =
    movie.type === 'movie' ? 'فيلم' :
    movie.type === 'series' ? 'مسلسل' : 'رسوم متحركة';

  const stars = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <>
      {/* Backdrop */}
      <div className="relative w-full h-[520px] md:h-[620px] overflow-hidden">
        <img
          src={detail.backdrop}
          alt={movie.title}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />

        {/* Content */}
        <div className="absolute bottom-0 right-0 left-0 px-6 lg:px-16 pb-12">
          <div className="max-w-3xl">
            {/* Tags */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="bg-[#C9A961] text-white text-xs font-black px-3 py-1 rounded-full">{typeLabel}</span>
              {movie.badge && (
                <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-bold px-3 py-1 rounded-full border border-white/30">{movie.badge}</span>
              )}
              <span className="bg-white/10 backdrop-blur-sm text-white text-xs font-bold px-3 py-1 rounded-full">{detail.ageRating}</span>
            </div>

            {/* Title */}
            <h1 className="text-white text-4xl md:text-5xl font-black mb-3 leading-tight drop-shadow-lg">
              {movie.title}
            </h1>

            {/* Meta */}
            <div className="flex items-center gap-4 mb-4 flex-wrap text-sm text-gray-300">
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line text-[#C9A961]" /></div>
                {movie.year}
              </span>
              <span className="text-white/30">•</span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line text-[#C9A961]" /></div>
                {movie.duration}
              </span>
              <span className="text-white/30">•</span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-translate-2 text-[#C9A961]" /></div>
                {movie.language}
              </span>
              <span className="text-white/30">•</span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line text-[#C9A961]" /></div>
                {detail.country}
              </span>
            </div>

            {/* Rating */}
            <div className="flex items-center gap-3 mb-5">
              <div className="flex items-center gap-1">
                {stars.map(s => (
                  <div key={s} className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-star-${s <= Math.round(movie.rating) ? 'fill' : 'line'} text-[#C9A961] text-sm`} />
                  </div>
                ))}
              </div>
              <span className="text-[#C9A961] font-black text-2xl">{movie.rating}</span>
              <span className="text-gray-400 text-sm">/10</span>
              <span className="text-gray-400 text-sm">({detail.reviews.length * 847 + 1203} تقييم)</span>
            </div>

            {/* Genre tags */}
            <div className="flex gap-2 flex-wrap mb-6">
              {movie.genre.map(g => (
                <span key={g} className="bg-white/10 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full border border-white/20">{g}</span>
              ))}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowPayment(true)}
                className="flex items-center gap-3 bg-[#C9A961] hover:bg-[#b8922e] text-white px-8 py-4 rounded-xl font-black text-base cursor-pointer transition-all whitespace-nowrap"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-play-circle-fill text-2xl" />
                </div>
                شاهد الآن — 25 درهم
              </button>

              <button
                onClick={() => setLiked(l => !l)}
                className={`flex items-center gap-2 px-5 py-4 rounded-xl font-bold text-sm cursor-pointer transition-all whitespace-nowrap border-2 ${liked ? 'bg-red-500/20 border-red-500 text-red-400' : 'bg-white/10 border-white/30 text-white hover:bg-white/20'}`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${liked ? 'ri-heart-fill text-red-400' : 'ri-heart-line'} text-lg`} />
                </div>
                {liked ? 'في المفضلة' : 'إضافة للمفضلة'}
              </button>

              <button className="flex items-center gap-2 px-5 py-4 rounded-xl font-bold text-sm cursor-pointer bg-white/10 border-2 border-white/30 text-white hover:bg-white/20 transition-all whitespace-nowrap">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-share-line text-lg" />
                </div>
                مشاركة
              </button>
            </div>
          </div>
        </div>
      </div>

      {showPayment && (
        <MoviePaymentModal movie={movie} onClose={() => setShowPayment(false)} />
      )}
    </>
  );
}
